import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ConfirmDialog from '../components/ConfirmDialog';
import { 
  LogOut, 
  RefreshCw, 
  Search, 
  ChevronDown, 
  User, 
  Phone, 
  Mail, 
  Calendar,
  Clock,
  Filter,
  X,
  CheckCircle,
  AlertCircle,
  BarChart3,
  Trash2,
  Download
} from 'lucide-react';

// Helper function to get status color classes
const getStatusColor = (status) => {
  switch (status) {
    case 'pending': return 'bg-yellow-100 text-yellow-800';
    case 'contacted': return 'bg-blue-100 text-blue-800';
    case 'enrolled': return 'bg-green-100 text-green-800';
    case 'closed': return 'bg-gray-100 text-gray-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

// StatusBadge component defined outside of AdminDashboardPage
const StatusBadge = ({ status }) => (
  <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(status)}`}>
    {status}
  </span>
);

const AdminDashboardPage = () => {
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [trashDialog, setTrashDialog] = useState(null); // { ids }
  const [trashing, setTrashing] = useState(false);
  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchConsultations = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(
        `${API_URL}/api/admin/consultations`,
        { credentials: 'include' }
      );

      if (response.status === 401) {
        navigate('/admin');
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch consultations');
      }

      const data = await response.json();
      setConsultations(data);
    } catch (err) {
      setError('Failed to load consultations. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [API_URL, navigate]);

  useEffect(() => {
    fetchConsultations();
  }, [fetchConsultations]);

  const handleLogout = async () => {
    try { await fetch(`${API_URL}/api/admin/logout`, { method: 'POST', credentials: 'include' }); } catch { /* noop */ }
    navigate('/admin');
  };

  const updateStatus = async (consultationId, newStatus) => {
    setUpdatingStatus(consultationId);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/consultations/${consultationId}/status?status=${newStatus}`,
        { method: 'PATCH', credentials: 'include' }
      );

      if (response.ok) {
        setConsultations(prev => 
          prev.map(c => c.id === consultationId ? { ...c, status: newStatus } : c)
        );
        if (selectedConsultation?.id === consultationId) {
          setSelectedConsultation(prev => ({ ...prev, status: newStatus }));
        }
      }
    } catch (err) {
      console.error('Failed to update status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const filteredConsultations = consultations.filter(c => {
    const matchesSearch = 
      c.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.mobile?.includes(searchTerm);
    const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const toggleSelect = (id) => setSelectedIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const allSelected = filteredConsultations.length > 0 && filteredConsultations.every((c) => selectedIds.includes(c.id));
  const toggleSelectAll = () => {
    if (allSelected) setSelectedIds((s) => s.filter((id) => !filteredConsultations.some((c) => c.id === id)));
    else setSelectedIds((s) => [...new Set([...s, ...filteredConsultations.map((c) => c.id)])]);
  };

  const moveToTrash = async (ids) => {
    setTrashing(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/consultations/trash`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids, admin_name: 'Admin' }),
      });
      if (res.ok) {
        setConsultations((prev) => prev.filter((c) => !ids.includes(c.id)));
        setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));
        if (selectedConsultation && ids.includes(selectedConsultation.id)) setSelectedConsultation(null);
      }
    } catch { /* noop */ } finally {
      setTrashing(false);
      setTrashDialog(null);
    }
  };

  const exportCSV = () => {
    if (!consultations.length) return;
    const esc = (v) => {
      const s = v === null || v === undefined ? '' : String(v);
      return `"${s.replace(/"/g, '""')}"`;
    };
    const header = [
      'Name', 'Email', 'Mobile', 'WhatsApp', 'City/Country',
      'Consultation For', 'Primary Concern', 'Source', 'Status', 'Message', 'Submitted On'
    ];
    const rows = consultations.map((c) => [
      c.full_name,
      c.email,
      c.mobile,
      c.whatsapp,
      c.country,
      Array.isArray(c.consultation_for) ? c.consultation_for.join('; ') : c.consultation_for,
      c.primary_goal,
      c.source || 'direct',
      c.status,
      c.additional_info,
      c.created_at ? new Date(c.created_at).toLocaleString('en-IN') : '',
    ].map(esc).join(','));
    const csv = [header.map(esc).join(','), ...rows].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `tcube_consultations_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const categorize = (s) => {
    const src = (s || 'direct').toLowerCase();
    if (src.includes('calc')) return 'Calculators';
    if (src.includes('hero') || src.includes('quiz') || src === 'direct') return 'Homepage';
    if (src.includes('nav') || src.includes('footer') || src.includes('announcement') || src.includes('mobile') || src.includes('drawer')) return 'Navigation';
    return 'Service Pages';
  };

  const sourceCounts = consultations.reduce((acc, c) => {
    const s = c.source || 'direct';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});
  const topSources = Object.entries(sourceCounts).sort((a, b) => b[1] - a[1]).slice(0, 8);
  const maxSource = topSources.length ? topSources[0][1] : 1;
  const categoryTotals = consultations.reduce((acc, c) => {
    const cat = categorize(c.source);
    acc[cat] = (acc[cat] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-[#E2E8F0] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-[#1F2933]">Consultation Dashboard</h1>
              <p className="text-sm text-[#64748B]">T Cube Fitness Admin</p>
            </div>
            <div className="flex items-center gap-3">
              <a
                href="/admin/health-analyses"
                className="hidden sm:inline-flex items-center gap-2 px-3 py-2 text-[#1E88E5] hover:bg-[#EFF6FF] rounded-lg text-sm font-semibold"
                data-testid="link-health-analyses"
              >
                Health Analyses
              </a>
              <a
                href="/admin/email-leads"
                className="hidden sm:inline-flex items-center gap-2 px-3 py-2 text-[#1E88E5] hover:bg-[#EFF6FF] rounded-lg text-sm font-semibold"
                data-testid="link-email-leads"
              >
                Email Leads
              </a>
              <a
                href="/admin/trash"
                className="hidden sm:inline-flex items-center gap-2 px-3 py-2 text-[#64748B] hover:bg-[#F1F5F9] rounded-lg text-sm font-semibold"
                data-testid="link-recycle-bin"
              >
                <Trash2 className="w-4 h-4" /> Recycle Bin
              </a>
              <button
                onClick={exportCSV}
                disabled={!consultations.length}
                className="hidden sm:inline-flex items-center gap-2 px-3 py-2 text-emerald-600 hover:bg-emerald-50 rounded-lg text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="export-consultations-csv-btn"
                title="Download all consultations as CSV"
              >
                <Download className="w-4 h-4" /> Export CSV
              </button>
              <button
                onClick={fetchConsultations}
                disabled={loading}
                className="p-2 text-[#64748B] hover:text-[#1E88E5] hover:bg-[#F1F5F9] rounded-lg transition-colors"
                data-testid="refresh-btn"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 text-[#64748B] hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {['pending', 'contacted', 'enrolled', 'closed'].map(status => {
            const count = consultations.filter(c => c.status === status).length;
            return (
              <div 
                key={status}
                className="bg-white rounded-xl p-4 shadow-sm border border-[#E2E8F0]"
                data-testid={`stat-${status}`}
              >
                <p className="text-sm text-[#64748B] capitalize">{status}</p>
                <p className="text-2xl font-bold text-[#1F2933]">{count}</p>
              </div>
            );
          })}
        </div>

        {/* Conversion Sources Analytics */}
        {!loading && consultations.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-[#E2E8F0] p-4 sm:p-5 mb-6" data-testid="conversion-analytics">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-5 h-5 text-[#1E88E5]" />
              <h2 className="font-bold text-[#1F2933]">Conversion Sources</h2>
              <span className="text-xs text-[#64748B]">{consultations.length} total consultations</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
              {Object.entries(categoryTotals).map(([cat, count]) => (
                <div key={cat} className="bg-[#F8FAFC] rounded-lg p-3 border border-[#E2E8F0]" data-testid={`source-cat-${cat.replace(/\s+/g, '-').toLowerCase()}`}>
                  <p className="text-xs text-[#64748B]">{cat}</p>
                  <p className="text-xl font-bold text-[#1F2933]">{count}</p>
                </div>
              ))}
            </div>
            <div className="space-y-2">
              {topSources.map(([src, count]) => (
                <div key={src} className="flex items-center gap-3" data-testid={`source-bar-${src}`}>
                  <span className="text-xs text-[#64748B] w-36 sm:w-44 truncate" title={src}>{src}</span>
                  <div className="flex-1 bg-[#F1F5F9] rounded-full h-2.5 overflow-hidden">
                    <div className="bg-[#1E88E5] h-full rounded-full" style={{ width: `${Math.max((count / maxSource) * 100, 4)}%` }} />
                  </div>
                  <span className="text-xs font-semibold text-[#1F2933] w-8 text-right">{count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-[#E2E8F0] p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-[#CBD5E1] rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent outline-none"
                data-testid="search-input"
              />
            </div>
            
            {/* Status Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="pl-10 pr-8 py-2.5 border border-[#CBD5E1] rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent outline-none appearance-none bg-white min-w-[150px]"
                data-testid="status-filter"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="contacted">Contacted</option>
                <option value="enrolled">Enrolled</option>
                <option value="closed">Closed</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#94A3B8] pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-center gap-3" data-testid="error-message">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-xl shadow-sm border border-[#E2E8F0] p-12 text-center">
            <RefreshCw className="w-8 h-8 text-[#1E88E5] animate-spin mx-auto mb-4" />
            <p className="text-[#64748B]">Loading consultations...</p>
          </div>
        )}

        {/* Bulk toolbar */}
        <AnimatePresence>
          {selectedIds.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              className="bg-[#1F2933] text-white rounded-xl p-3 mb-4 flex items-center justify-between" data-testid="dashboard-bulk-toolbar">
              <span className="text-sm font-medium ml-2">{selectedIds.length} selected</span>
              <button onClick={() => setTrashDialog({ ids: [...selectedIds] })} className="inline-flex items-center gap-1.5 px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-semibold" data-testid="dashboard-bulk-trash-btn">
                <Trash2 className="w-4 h-4" /> Move to Trash
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Consultations Table */}
        {!loading && filteredConsultations.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-[#E2E8F0] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="consultations-table">
                <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                  <tr>
                    <th className="px-4 py-4"><input type="checkbox" checked={allSelected} onChange={toggleSelectAll} className="w-4 h-4 rounded" data-testid="dashboard-select-all" /></th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-[#64748B]">Client</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-[#64748B]">Contact</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-[#64748B]">Goal</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-[#64748B]">Source</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-[#64748B]">Status</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-[#64748B]">Date</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-[#64748B]">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]">
                  {filteredConsultations.map((consultation) => (
                    <tr 
                      key={consultation.id} 
                      className="hover:bg-[#F8FAFC] transition-colors"
                      data-testid={`consultation-row-${consultation.id}`}
                    >
                      <td className="px-4 py-4"><input type="checkbox" checked={selectedIds.includes(consultation.id)} onChange={() => toggleSelect(consultation.id)} className="w-4 h-4 rounded" data-testid={`dashboard-select-${consultation.id}`} /></td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#E2E8F0] rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-[#64748B]" />
                          </div>
                          <div>
                            <p className="font-medium text-[#1F2933]">{consultation.full_name}</p>
                            <p className="text-sm text-[#64748B]">{consultation.age}y, {consultation.gender}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2 text-sm text-[#64748B]">
                            <Phone className="w-4 h-4" />
                            <span>{consultation.mobile}</span>
                          </div>
                          {consultation.email && (
                            <div className="flex items-center gap-2 text-sm text-[#64748B]">
                              <Mail className="w-4 h-4" />
                              <span className="truncate max-w-[150px]">{consultation.email}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-[#1F2933] capitalize">
                          {consultation.primary_goal?.replace('-', ' ') || 'Not specified'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {consultation.source ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-[#EFF6FF] text-[#1E88E5] text-xs font-semibold" data-testid={`consultation-source-${consultation.id}`}>
                            {consultation.source}
                          </span>
                        ) : (
                          <span className="text-xs text-[#94A3B8]">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <StatusBadge status={consultation.status} />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-sm text-[#64748B]">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(consultation.created_at)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setSelectedConsultation(consultation)}
                            className="px-3 py-1.5 text-sm font-medium text-[#1E88E5] hover:bg-[#E3F2FD] rounded-lg transition-colors"
                            data-testid={`view-details-${consultation.id}`}
                          >
                            View Details
                          </button>
                          <button
                            onClick={() => setTrashDialog({ ids: [consultation.id] })}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Move to Recycle Bin"
                            data-testid={`delete-${consultation.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredConsultations.length === 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-[#E2E8F0] p-12 text-center" data-testid="empty-state">
            <div className="w-16 h-16 bg-[#F1F5F9] rounded-full flex items-center justify-center mx-auto mb-4">
              <User className="w-8 h-8 text-[#94A3B8]" />
            </div>
            <h3 className="text-lg font-semibold text-[#1F2933] mb-2">No consultations found</h3>
            <p className="text-[#64748B]">
              {searchTerm || statusFilter !== 'all' 
                ? 'Try adjusting your search or filters' 
                : 'New consultation requests will appear here'}
            </p>
          </div>
        )}
      </main>

      {/* Detail Modal */}
      {selectedConsultation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="detail-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-[#E2E8F0] px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-[#1F2933]">Consultation Details</h2>
              <button
                onClick={() => setSelectedConsultation(null)}
                className="p-2 hover:bg-[#F1F5F9] rounded-lg transition-colors"
                data-testid="close-modal-btn"
              >
                <X className="w-5 h-5 text-[#64748B]" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Client Info */}
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-[#E3F2FD] rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="w-8 h-8 text-[#1E88E5]" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#1F2933]">{selectedConsultation.full_name}</h3>
                  <p className="text-[#64748B]">{selectedConsultation.age} years, {selectedConsultation.gender}</p>
                  <p className="text-[#64748B]">{selectedConsultation.country}</p>
                </div>
              </div>

              {/* Contact Details */}
              <div className="bg-[#F8FAFC] rounded-xl p-4 space-y-3">
                <h4 className="font-semibold text-[#1F2933]">Contact Information</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-[#64748B]" />
                    <span className="text-[#1F2933]">{selectedConsultation.mobile}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-green-600" />
                    <span className="text-[#1F2933]">WhatsApp: {selectedConsultation.whatsapp}</span>
                  </div>
                  {selectedConsultation.email && (
                    <div className="flex items-center gap-2 sm:col-span-2">
                      <Mail className="w-4 h-4 text-[#64748B]" />
                      <span className="text-[#1F2933]">{selectedConsultation.email}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Beneficiaries */}
              {selectedConsultation.beneficiaries && selectedConsultation.beneficiaries.length > 0 ? (
                <div className="space-y-3">
                  <h4 className="font-semibold text-[#1F2933]">Beneficiaries ({selectedConsultation.beneficiaries.length})</h4>
                  <div className="grid gap-3">
                    {selectedConsultation.beneficiaries.map((b, idx) => {
                      const bmi = b.height_cm && b.weight_kg
                        ? (b.weight_kg / Math.pow(b.height_cm / 100, 2)).toFixed(1)
                        : '-';
                      return (
                        <div key={idx} className="bg-[#F8FAFC] rounded-xl p-4 border border-[#E2E8F0]" data-testid={`beneficiary-detail-${idx}`}>
                          <div className="flex items-center justify-between mb-3">
                            <span className="font-semibold text-[#1F2933]">{b.relationship}</span>
                            <span className="text-xs px-2.5 py-0.5 rounded-full bg-[#E3F2FD] text-[#1E88E5] capitalize">{(b.gender || '').replace(/_/g, ' ')}</span>
                          </div>
                          <div className="grid grid-cols-4 gap-3">
                            <div><p className="text-xs text-[#64748B]">Age</p><p className="text-[#1F2933] font-medium">{b.age ? `${b.age} yrs` : '-'}</p></div>
                            <div><p className="text-xs text-[#64748B]">Height</p><p className="text-[#1F2933] font-medium">{b.height_cm ? `${b.height_cm} cm` : '-'}</p></div>
                            <div><p className="text-xs text-[#64748B]">Weight</p><p className="text-[#1F2933] font-medium">{b.weight_kg ? `${b.weight_kg} kg` : '-'}</p></div>
                            <div><p className="text-xs text-[#64748B]">BMI</p><p className="text-[#1F2933] font-medium">{bmi}</p></div>
                          </div>
                          {b.primary_concern && (
                            <div className="mt-3 pt-3 border-t border-[#E2E8F0]">
                              <p className="text-xs text-[#64748B]">Primary Concern</p>
                              <p className="text-[#1F2933] font-medium capitalize">{String(b.primary_concern).replace(/_/g, ' ')}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                (selectedConsultation.height_cm || selectedConsultation.weight_kg || selectedConsultation.age || (selectedConsultation.consultation_for && selectedConsultation.consultation_for.length > 0)) && (
                  <div className="bg-[#F8FAFC] rounded-xl p-4 space-y-3">
                    <h4 className="font-semibold text-[#1F2933]">Physical & Assessment</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div>
                        <p className="text-xs text-[#64748B]">Age</p>
                        <p className="text-[#1F2933] font-medium">{selectedConsultation.age ? `${selectedConsultation.age} yrs` : '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#64748B]">Height</p>
                        <p className="text-[#1F2933] font-medium">{selectedConsultation.height_cm ? `${selectedConsultation.height_cm} cm` : '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#64748B]">Weight</p>
                        <p className="text-[#1F2933] font-medium">{selectedConsultation.weight_kg ? `${selectedConsultation.weight_kg} kg` : '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#64748B]">BMI</p>
                        <p className="text-[#1F2933] font-medium">
                          {selectedConsultation.height_cm && selectedConsultation.weight_kg
                            ? (selectedConsultation.weight_kg / Math.pow(selectedConsultation.height_cm / 100, 2)).toFixed(1)
                            : '-'}
                        </p>
                      </div>
                    </div>
                    {selectedConsultation.consultation_for && selectedConsultation.consultation_for.length > 0 && (
                      <div>
                        <p className="text-xs text-[#64748B] mb-1.5">Consultation for</p>
                        <div className="flex flex-wrap gap-2">
                          {selectedConsultation.consultation_for.map((who, idx) => (
                            <span key={idx} className="px-3 py-1 bg-[#E3F2FD] text-[#1E88E5] rounded-full text-sm">{who}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )
              )}

              {/* Health Info */}
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-[#1F2933] mb-2">Primary Goal</h4>
                  <p className="text-[#64748B] capitalize">{selectedConsultation.primary_goal?.replace('-', ' ') || 'Not specified'}</p>
                </div>

                {selectedConsultation.source && (
                  <div>
                    <h4 className="font-semibold text-[#1F2933] mb-2">Conversion Source</h4>
                    <span className="inline-flex items-center px-3 py-1 bg-[#EFF6FF] text-[#1E88E5] rounded-full text-sm font-semibold" data-testid="modal-source-badge">
                      {selectedConsultation.source}
                    </span>
                  </div>
                )}

                {selectedConsultation.health_conditions?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-[#1F2933] mb-2">Health Conditions</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedConsultation.health_conditions.map((condition, idx) => (
                        <span key={idx} className="px-3 py-1 bg-[#FEF3C7] text-[#92400E] rounded-full text-sm capitalize">
                          {condition}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {selectedConsultation.on_medications === 'yes' && (
                  <div>
                    <h4 className="font-semibold text-[#1F2933] mb-2">Medications</h4>
                    <p className="text-[#64748B]">{selectedConsultation.medications_details || 'Details not provided'}</p>
                  </div>
                )}

                {selectedConsultation.tried_before && (
                  <div>
                    <h4 className="font-semibold text-[#1F2933] mb-2">Previous Attempts</h4>
                    <p className="text-[#64748B] capitalize">{selectedConsultation.tried_before}</p>
                  </div>
                )}

                {selectedConsultation.additional_info && (
                  <div>
                    <h4 className="font-semibold text-[#1F2933] mb-2">Additional Information</h4>
                    <p className="text-[#64748B]">{selectedConsultation.additional_info}</p>
                  </div>
                )}
              </div>

              {/* Status & Date */}
              <div className="flex items-center justify-between border-t border-[#E2E8F0] pt-4">
                <div className="flex items-center gap-2 text-sm text-[#64748B]">
                  <Clock className="w-4 h-4" />
                  <span>Submitted: {formatDate(selectedConsultation.created_at)}</span>
                </div>
                <StatusBadge status={selectedConsultation.status} />
              </div>

              {/* Status Update */}
              <div className="border-t border-[#E2E8F0] pt-4">
                <h4 className="font-semibold text-[#1F2933] mb-3">Update Status</h4>
                <div className="flex flex-wrap gap-2">
                  {['pending', 'contacted', 'enrolled', 'closed'].map(status => (
                    <button
                      key={status}
                      onClick={() => updateStatus(selectedConsultation.id, status)}
                      disabled={selectedConsultation.status === status || updatingStatus === selectedConsultation.id}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        selectedConsultation.status === status
                          ? 'bg-[#1E88E5] text-white'
                          : 'bg-[#F1F5F9] text-[#64748B] hover:bg-[#E2E8F0]'
                      } ${updatingStatus === selectedConsultation.id ? 'opacity-50' : ''}`}
                      data-testid={`status-btn-${status}`}
                    >
                      {updatingStatus === selectedConsultation.id && selectedConsultation.status !== status
                        ? 'Updating...'
                        : status.charAt(0).toUpperCase() + status.slice(1)}
                      {selectedConsultation.status === status && (
                        <CheckCircle className="inline-block ml-1 w-4 h-4" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!trashDialog}
        title={trashDialog && trashDialog.ids.length > 1 ? `Move ${trashDialog.ids.length} consultations to the Recycle Bin?` : 'Move this consultation request to the Recycle Bin?'}
        message="You can restore it within 30 days. After 30 days, it will be permanently deleted."
        confirmLabel="Move to Trash"
        cancelLabel="Cancel"
        variant="warning"
        loading={trashing}
        onCancel={() => setTrashDialog(null)}
        onConfirm={() => moveToTrash(trashDialog.ids)}
      />
    </div>
  );
};

export default AdminDashboardPage;
